/**
 * GA4 E-Commerce Event Tracking Helpers
 *
 * Provides consistent item structure and event tracking
 * for Google Analytics 4 Enhanced Ecommerce events.
 */

import {
    buildPurchaseDedupeKey,
    pushToDataLayer,
    runTrackedEventOnce,
} from '@/utils/trackingEventHelpers';

export interface VoucherItem {
    type: 'physical' | 'digital';
    amount: number;
    background?: string;
    greeting?: string;
    personalNote?: string;
    quantity?: number;
    product_name?: string;
}

export interface GA4Item {
    item_id: string;
    item_name: string;
    item_category: string;
    item_brand: string;
    price: number;
    quantity: number;
    item_variant?: string;
}

export interface PurchaseEventData {
    transaction_id: string;
    value: number;
    customer_email?: string;
    customer_first_name?: string;
    customer_last_name?: string;
    payment_method?: string;
    shipping_tier?: string;
    customer_type?: string;
    voucher_type?: string;
    promotion_used?: string;
    promotion_code?: string | null;
    discount_amount?: number;
    device_type?: string;
    location_country?: string;
    has_personal_note?: string;
    has_greeting?: string;
    voucher_amount?: number;
    shipping_costs?: number;
    voucher_count?: number;
    items: GA4Item[];
    event_id?: string;
}

/**
 * Build a GA4-compliant item object from a voucher configuration
 */
export const buildGa4VoucherItem = (voucher: VoucherItem, index?: number): GA4Item => {
    let itemId = `voucher-${voucher.type}`;
    if (voucher.background) {
        itemId += `-${voucher.background}`;
    }
    if (index !== undefined) {
        itemId += `-${index + 1}`;
    }

    return {
        item_id: itemId,
        item_name: voucher.type === 'physical'
            ? 'GLOWCARD Gutschein-Karte'
            : (voucher.product_name || 'GLOWCARD Digitaler Gutschein'),
        item_category: 'Geschenkgutschein',
        item_brand: 'GLOWCARD',
        price: Number(voucher.amount),
        quantity: voucher.quantity || 1,
        item_variant: voucher.background || '',
    };
};

/**
 * Build GA4 item for shipping costs
 */
export const buildShippingItem = (shippingCost: number): GA4Item => {
    return {
        item_id: 'shipping-costs',
        item_name: 'Verpackungs- und Versandkosten',
        item_category: 'Versand',
        item_brand: 'GLOWCARD',
        price: Number(shippingCost),
        quantity: 1,
    };
};

/**
 * Track view_item event
 */
export const trackViewItem = (voucher: VoucherItem): void => {
    const items = [buildGa4VoucherItem(voucher)];

    pushToDataLayer({
        event: 'view_item',
        currency: 'EUR',
        value: Number(voucher.amount),
        ecommerce: {
            items,
        },
    });
};

/**
 * Track add_to_cart event
 */
export const trackAddToCart = (voucher: VoucherItem): void => {
    const items = [buildGa4VoucherItem(voucher)];
    const value = Number(voucher.amount) * (voucher.quantity || 1);

    pushToDataLayer({
        event: 'add_to_cart',
        currency: 'EUR',
        value,
        ecommerce: {
            items,
        },
    });
};

/**
 * Track remove_from_cart event
 */
export const trackRemoveFromCart = (voucher: VoucherItem): void => {
    const items = [buildGa4VoucherItem(voucher)];
    const value = Number(voucher.amount) * (voucher.quantity || 1);

    pushToDataLayer({
        event: 'remove_from_cart',
        currency: 'EUR',
        value,
        ecommerce: {
            items,
        },
    });
};

/**
 * Track view_cart event
 */
export const trackViewCart = (vouchers: VoucherItem[], total: number, shippingCost?: number): void => {
    const items = vouchers.map((voucher, index) => buildGa4VoucherItem(voucher, index));

    if (shippingCost && shippingCost > 0) {
        items.push(buildShippingItem(shippingCost));
    }

    pushToDataLayer({
        event: 'view_cart',
        currency: 'EUR',
        value: Number(total),
        ecommerce: {
            items,
        },
    });
};

/**
 * Track begin_checkout event
 */
export const trackBeginCheckout = (
    items: GA4Item[],
    total: number,
    paymentIntentId?: string,
): void => {
    pushToDataLayer({
        event: 'begin_checkout',
        currency: 'EUR',
        value: Number(total),
        ecommerce: {
            items,
        },
    });
};

/**
 * Track purchase event (once per order, survives page reload)
 */
export const trackPurchase = (data: PurchaseEventData): void => {
    const dedupeKey = buildPurchaseDedupeKey(data.transaction_id);

    runTrackedEventOnce(dedupeKey, 'local', () => {
        const eventData: Record<string, unknown> = {
            event: 'purchase',
            transaction_id: data.transaction_id,
            currency: 'EUR',
            value: Number(data.value),
            customer_email: data.customer_email ?? '',
            customer_first_name: data.customer_first_name ?? 'Kunde',
            customer_last_name: data.customer_last_name ?? '',
            payment_method: data.payment_method ?? 'unknown',
            shipping_tier: data.shipping_tier ?? 'digital',
            customer_type: data.customer_type ?? 'new',
            voucher_type: data.voucher_type ?? 'digital',
            promotion_used: data.promotion_used ?? 'no',
            promotion_code: data.promotion_code ?? null,
            discount_amount: data.discount_amount ?? 0,
            device_type: data.device_type,
            location_country: data.location_country ?? 'DE',
            has_personal_note: data.has_personal_note ?? 'no',
            has_greeting: data.has_greeting ?? 'no',
            voucher_amount: data.voucher_amount,
            shipping_costs: data.shipping_costs ?? 0,
            voucher_count: data.voucher_count ?? 0,
            ecommerce: {
                items: data.items,
            },
        };

        if (data.event_id) {
            eventData.event_id = data.event_id;
        }

        pushToDataLayer(eventData);
    });
};

/**
 * Track add_payment_info event
 */
export const trackAddPaymentInfo = (
    vouchers: VoucherItem[],
    total: number,
    paymentType: string = 'stripe',
): void => {
    const items = vouchers.map((voucher, index) => buildGa4VoucherItem(voucher, index));

    pushToDataLayer({
        event: 'add_payment_info',
        currency: 'EUR',
        value: Number(total),
        payment_type: paymentType,
        ecommerce: {
            items,
        },
    });
};
